import { identity, isEqual, pickBy } from "lodash";
import memoizeOne from "memoize-one";
import { createSelector } from "reselect";

import { Filter, IFilters } from "constants/filters";
import { Right } from "constants/right";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { filtersToOmitInURLForRoute } from "helpers/tickets";
import {
  EventType,
  IEvent,
  IOldNew,
  ISimilarTicket,
  ITicket,
  ITicketSpamStatus,
  TicketSilo,
} from "interfaces/entities/ticket";
import { ITicketSorting } from "interfaces/sorting";
import { IWithAgentsState } from "store/entities/agents";
import { selectCanCreateCustomViews, selectCustomViews } from "store/entities/custom-views/selectors";
import { selectAreMailboxesSetUp } from "store/entities/email-domains";
import {
  selectHasDoneLiveChatSelectiveImportBefore,
  selectHasEverDoneFullLiveChatDataImport,
  selectIntegrationCreatedTicket,
} from "store/entities/integrations";
import { IWithSessionState, hasRight } from "store/features/session";
import { IWithRequestsState, createRequestFetchingSelector } from "store/requests/selectors";

import { FETCH_ORIGINAL, FETCH_UNSTRIPPED } from "./actions";
import { ITicketsViewState } from "./reducer";

export interface IWithTicketsViewState {
  views: {
    tickets: ITicketsViewState;
  };
}

export function getIsFetching(state: IWithRequestsState): boolean {
  return createRequestFetchingSelector(["FETCH_COLLECTION_TICKET"])(state);
}

export function getAnyDataFetched(state: IWithTicketsViewState): boolean {
  return state.views.tickets.anyDataFetched;
}

export function getFocusedColumn(state: IWithTicketsViewState): number {
  return state.views.tickets.focusedColumn;
}

function getSortedTicketsIds(state: IWithTicketsViewState): string[] {
  return state.views.tickets.sortedIds;
}

export function getTicketsData(state: IWithTicketsViewState): { [key: string]: ITicket } {
  return state.views.tickets.data;
}

export function getCurrentPage(state: IWithTicketsViewState): number {
  return state.views.tickets.currentPage;
}

export function getTotalPages(state: IWithTicketsViewState): number {
  return state.views.tickets.totalPages;
}

export function getTotalResults(state: IWithTicketsViewState): number {
  return state.views.tickets.totalResults;
}

function getTicketsBySortedIds(sortedTicketIds: string[], tickets: { [key: string]: ITicket }): ITicket[] {
  return sortedTicketIds.map((ticketId: string) => tickets[ticketId]);
}

export const cachedGetTicketsSelector = createSelector(getSortedTicketsIds, getTicketsData, getTicketsBySortedIds);

export function getTickets(state: IWithTicketsViewState): ITicket[] {
  return cachedGetTicketsSelector(state);
}

const addSyntheticSpamEvent = (ticket: ITicket): IEvent[] => {
  const reasonSource = ticket.events.find(
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    ({ type, spam }) => type === "spam" && spam.old.reason !== null && spam.new.status === false
  );

  const shouldAddSyntheticEvent = reasonSource || ticket.spam.reason !== null;

  const spam: IOldNew<ITicketSpamStatus> = reasonSource
    ? // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      { old: { ...reasonSource.spam.old }, new: { ...reasonSource.spam.old } }
    : { old: { ...ticket.spam, status: true }, new: { ...ticket.spam, status: true } };

  const syntheticSpamEvent: IEvent = {
    id: "-1",
    date: ticket.date,
    type: EventType.Spam,
    spam,
    spamReasonSource: true,
  };

  const eventIndex = ticket.events.findIndex(event => event.type === "message");
  return shouldAddSyntheticEvent
    ? [...ticket.events.slice(0, eventIndex + 1), syntheticSpamEvent, ...ticket.events.slice(eventIndex + 1)]
    : ticket.events;
};

const enhanceWithSpamInformation = memoizeOne((ticket: ITicket): ITicket => {
  if (!ticket) {
    // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'ITicket'.
    return null;
  }
  const events = addSyntheticSpamEvent(ticket);

  return { ...ticket, events };
});

export function getTicket(state: IWithTicketsViewState): ITicket | null {
  return state.views.tickets.current ? enhanceWithSpamInformation(state.views.tickets.current) : null;
}

export const selectEventById = createSelector(
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  [getTicket, (_: any, eventId: string) => eventId],
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (ticket: any, eventId: any) => ticket.events.find((event: any) => event.id === eventId)
);

export function getRoute(state: IWithTicketsViewState): TicketsRoutes {
  return state.views.tickets.route;
}

export function getDataFetchedForRoute(state: IWithTicketsViewState): TicketsRoutes | null {
  return state.views.tickets.dataFetchedForRoute;
}

export function getFilters(state: IWithTicketsViewState): IFilters {
  return state.views.tickets.filters;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getActiveFilters = createSelector(getFilters, (filters: any) => pickBy(filters, identity));

export function getFiltersOrder(state: IWithTicketsViewState): Filter[] {
  return state.views.tickets.filtersOrder;
}

export function getSelectedTicketIds(state: IWithTicketsViewState): string[] {
  return state.views.tickets.selectedIds;
}

export function getSearchQuery(state: IWithTicketsViewState): string {
  return state.views.tickets.searchQuery;
}

export function getSilo(state: IWithTicketsViewState): TicketSilo {
  return state.views.tickets.silo;
}

export function getIsBatchUpdating(state: IWithRequestsState): boolean {
  return createRequestFetchingSelector(["VIEWS/TICKETS/UPDATE_BATCH"])(state);
}

export function selectIsFetchingClosestTicket(state: IWithRequestsState): boolean {
  return createRequestFetchingSelector(["ENTITIES/TICKET/FETCH_CLOSEST_TICKET"])(state);
}

export function getNavigationItemCount(state: IWithTicketsViewState): Partial<Record<TicketsRoutes, number>> {
  return state.views.tickets.navigationItemCount;
}

export function isSendingMessage(state: IWithTicketsViewState): boolean {
  return state.views.tickets.sendingMessage;
}

export function isUnspamming(state: IWithTicketsViewState): boolean {
  return state.views.tickets.unspamming;
}

export function selectActiveCustomViewId(state: IWithTicketsViewState): string | null {
  return state.views.tickets.activeCustomViewId;
}

export const selectActiveCustomView = createSelector(selectCustomViews, selectActiveCustomViewId, (customViews, id) => {
  if (!id) {
    return null;
  }
  return customViews.data[id];
});

export const selectQualifiesForSavingAsCustomView = createSelector(
  [getActiveFilters, getRoute, selectActiveCustomViewId, selectCustomViews, selectCanCreateCustomViews],
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (activeFilters: any, route: any, activeCustomViewId: any, customViews: any, canCreateCustomViews: any) => {
    if (!canCreateCustomViews) {
      return false;
    }

    // custom view active
    if (activeCustomViewId && customViews.data[activeCustomViewId]) {
      return (
        Object.keys(activeFilters).length > 0 && !isEqual(activeFilters, customViews.data[activeCustomViewId].filters)
      );
    }

    // regular view active
    const visibleFilters = pickBy(
      activeFilters,
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      (_, filterName: Filter) => !filtersToOmitInURLForRoute[route].includes(filterName)
    );

    return Object.keys(visibleFilters).length > 0;
  }
);

export const selectShouldIgnoreRealtimeCounter = createSelector(
  [getActiveFilters, getRoute, getSearchQuery, selectActiveCustomViewId, selectCustomViews],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (activeFilters: any, route: TicketsRoutes, searchQuery: string, activeCustomViewId: string | null, customViews: any): boolean => {
    if (searchQuery) {
      return true;
    }

    if (activeCustomViewId && customViews.data[activeCustomViewId]) {
      return !isEqual(activeFilters, customViews.data[activeCustomViewId].filters);
    }

    const visibleFilters = pickBy(
      activeFilters,
      (_, filterName: Filter) => !filtersToOmitInURLForRoute[route]?.includes(filterName)
    );

    return Object.keys(visibleFilters).length > 0;
  }
);

export function selectLiveCounterForActiveView(state: IWithTicketsViewState): number | undefined {
  const { navigationItemCount, activeCustomViewId, route } = state.views.tickets;
  const key = (activeCustomViewId ?? route) as TicketsRoutes;
  return navigationItemCount[key];
}
export function selectCanCreateTickets(state: IWithSessionState & IWithAgentsState): boolean {
  return hasRight(state, Right.CreateTickets);
}

export function selectCanDeleteTickets(state: IWithSessionState & IWithAgentsState): boolean {
  return hasRight(state, Right.DeleteTickets);
}

export function selectCanManageTickets(state: IWithSessionState & IWithAgentsState): boolean {
  return hasRight(state, Right.ManageTickets);
}

export function selectSummarizingTickets(state: IWithTicketsViewState): { [key: string]: boolean } {
  // @ts-expect-error TS(2322): Type '{ [key: string]: boolean; } | undefined' is ... Remove this comment to see the full error message
  return state.views.tickets.summarizingTickets;
}

export function selectIsSummarizingTicketById(state: IWithTicketsViewState, ticketId: string): boolean {
  return state.views.tickets.summarizingTickets ? state.views.tickets.summarizingTickets[ticketId] : false;
}

export function selectRecentTickets(state: IWithTicketsViewState): ITicket[] {
  return state.views.tickets.recentTickets || [];
}

export function selectSuggestedTagIds(state: IWithTicketsViewState): string[] | null {
  return state.views.tickets.suggestedTagIds;
}

export function selectSimilarTickets(state: IWithTicketsViewState): ISimilarTicket[] | null {
  return state.views.tickets.similarTickets;
}

export function selectRecentTicketsAvailable(state: IWithTicketsViewState): boolean {
  return state.views.tickets.recentTickets !== null;
}

export function selectTicketsToMerge(state: IWithTicketsViewState): ITicket[] {
  return state.views.tickets.ticketsToMerge || [];
}

export function selectTicketsToMergeAvailable(state: IWithTicketsViewState): boolean {
  return state.views.tickets.ticketsToMerge !== null;
}

export const selectTicketsForwardedToHelpDesk = createSelector(
  [
    selectHasEverDoneFullLiveChatDataImport,
    selectHasDoneLiveChatSelectiveImportBefore,
    selectAreMailboxesSetUp,
    selectIntegrationCreatedTicket,
  ],
  (
    hasEverDoneFullLiveChatDataImport: boolean,
    selectiveImportDone: boolean,
    areMailboxesSetUp: boolean,
    integrationCreatedTicket: boolean
  ) => hasEverDoneFullLiveChatDataImport || selectiveImportDone || areMailboxesSetUp || integrationCreatedTicket
);

export function selectCurrentSorting(state: IWithTicketsViewState): ITicketSorting {
  return state.views.tickets.currentSorting;
}

export function getIsLoadingOriginalMessage(state: IWithRequestsState): boolean {
  return createRequestFetchingSelector([FETCH_ORIGINAL])(state);
}

export function getIsLoadingUnstrippedMessage(state: IWithRequestsState): boolean {
  return createRequestFetchingSelector([FETCH_UNSTRIPPED])(state);
}
